import React, { useState } from 'react';
import { RefreshCw, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { db, doc, getDoc } from '../../services/firebase';
import logo from '../../assets/logo.png';

// Official WhatsApp Vector Icon
function WhatsAppIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.888 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function MaintenancePage({
  message = '',
  estimatedUptime = '',
  shopPhone = '9939079107',
}) {
  const [checking, setChecking] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState(null);

  const cleanPhone = String(shopPhone || '9939079107').replace(/[^0-9]/g, '').slice(-10);
  const whatsappUrl = `https://wa.me/91${cleanPhone}?text=${encodeURIComponent(
    'Hello Lucy Garden, I need assistance regarding wholesale supply during portal maintenance.'
  )}`;

  const handleLiveCheck = async () => {
    setChecking(true);
    setStatusFeedback(null);
    try {
      const snap = await getDoc(doc(db, 'settings', 'app'));
      if (snap.exists() && !snap.data().maintenance) {
        setStatusFeedback({ type: 'online', text: 'System is back online! Redirecting...' });
        setTimeout(() => {
          window.location.reload();
        }, 800);
      } else {
        setStatusFeedback({
          type: 'waiting',
          text: 'Maintenance is still active. Please try again shortly.',
        });
        setTimeout(() => setStatusFeedback(null), 3000);
      }
    } catch (e) {
      setStatusFeedback({ type: 'error', text: 'Connection issue. Please check network.' });
      setTimeout(() => setStatusFeedback(null), 3000);
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-[#f8f7f4] dark:bg-[#121214] text-[#1a1917] dark:text-[#f5f4f0] flex flex-col justify-between p-4 sm:p-6 select-none font-sans">
      {/* Top Brand Logo Pill */}
      <div className="w-full max-w-md mx-auto flex items-center justify-center pt-3 sm:pt-6">
        <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-2xl bg-white dark:bg-[#1c1c21] border border-[#e2e0db] dark:border-[#2a2a32] shadow-xs">
          <img src={logo} alt="Lucy Garden" className="w-8 h-8 rounded-lg object-cover shadow-2xs" />
          <div className="text-left">
            <p className="text-xs font-black tracking-tight text-[#1a1917] dark:text-[#f5f4f0] leading-none">
              LUCY GARDEN
            </p>
            <p className="text-[9px] text-[#6b6860] dark:text-[#9c9890] font-medium leading-tight mt-0.5">
              Fresh Dairy Supply
            </p>
          </div>
        </div>
      </div>

      {/* Centered Maintenance Card */}
      <main className="w-full max-w-md mx-auto my-auto py-6">
        <div className="bg-white dark:bg-[#1c1c21] rounded-2xl border border-[#e2e0db] dark:border-[#2a2a32] shadow-sm p-6 sm:p-8 text-center space-y-5">
          
          {/* Maintenance Icon Badge */}
          <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 flex items-center justify-center text-amber-600 dark:text-amber-400">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
          </div>

          {/* Heading */}
          <div className="space-y-1.5">
            <h1 className="text-xl sm:text-2xl font-black text-[#1a1917] dark:text-[#f5f4f0] tracking-tight">
              Under Maintenance
            </h1>
            <p className="text-xs sm:text-sm text-[#6b6860] dark:text-[#a8a49c] leading-relaxed">
              We're performing scheduled updates to improve system performance. Online ordering will resume shortly.
            </p>
          </div>

          {/* Optional Admin Message / Estimated Time */}
          {(message || estimatedUptime) && (
            <div className="p-3.5 rounded-xl bg-[#faf9f6] dark:bg-[#23232a] border border-[#e8e6e1] dark:border-[#33333d] text-left space-y-1.5">
              {estimatedUptime && (
                <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-400">
                  <Clock size={13} className="shrink-0" />
                  <span>Expected Back: <strong className="font-semibold text-[#1a1917] dark:text-[#f5f4f0]">{estimatedUptime}</strong></span>
                </div>
              )}
              {message && (
                <div className="flex items-start gap-1.5 text-xs text-[#4a4843] dark:text-[#c5c3bc]">
                  <AlertCircle size={13} className="text-[#9c9890] shrink-0 mt-0.5" />
                  <span>{message}</span>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2.5 pt-1">
            {/* WhatsApp Support Button with Official Icon */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2.5 px-4 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] active:scale-98 text-white text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-xs"
            >
              <WhatsAppIcon className="w-4 h-4 text-white shrink-0" />
              <span>Contact on WhatsApp</span>
            </a>

            {/* Check Status Button */}
            <button
              type="button"
              onClick={handleLiveCheck}
              disabled={checking}
              className="w-full py-2.5 px-4 rounded-xl bg-white dark:bg-[#24242c] hover:bg-[#f5f4f0] dark:hover:bg-[#2c2c36] text-[#1a1917] dark:text-[#f5f4f0] border border-[#e2e0db] dark:border-[#363642] text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer disabled:opacity-50 active:scale-98"
            >
              <RefreshCw size={13} className={checking ? 'animate-spin text-[#1e3a5f]' : ''} />
              <span>{checking ? 'Checking...' : 'Check Server Status'}</span>
            </button>
          </div>

          {/* Live Check Feedback Notification */}
          {statusFeedback && (
            <div
              className={`p-2 rounded-lg text-xs font-medium border ${
                statusFeedback.type === 'online'
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300'
                  : statusFeedback.type === 'waiting'
                  ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300'
                  : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300'
              }`}
            >
              {statusFeedback.text}
            </div>
          )}

          {/* Urgent Phone Helpline */}
          <div className="pt-2 border-t border-[#f0eee9] dark:border-[#26262e] text-center">
            <p className="text-xs text-[#6b6860] dark:text-[#9c9890]">
              Urgent orders or delivery query?{' '}
              <a href={`tel:+91${cleanPhone}`} className="font-bold text-[#1e3a5f] dark:text-[#8cb8ff] hover:underline whitespace-nowrap">
                Call +91 {cleanPhone}
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer Branding & Makeward Labs Promotion */}
      <footer className="w-full max-w-md mx-auto text-center pb-4 pt-2 space-y-1">
        <p className="text-xs text-[#6b6860] dark:text-[#9c9890]">
          Software & Cloud Infrastructure by{' '}
          <a
            href="https://makeward.in"
            target="_blank"
            rel="noreferrer"
            className="font-bold text-[#1a1917] dark:text-[#f5f4f0] hover:text-[#1e3a5f] dark:hover:text-[#8cb8ff] transition-colors inline-flex items-center gap-1"
          >
            <span>Makeward Labs</span>
            <ExternalLink size={11} className="opacity-70" />
          </a>
        </p>
        <p className="text-[10px] text-[#9c9890] dark:text-[#6e6c66]">
          Lucy Garden Fresh Dairy Supply • makeward.in
        </p>
      </footer>
    </div>
  );
}
